import { Type } from "class-transformer";
import { IPeriode } from "../utils/dateUtils";
import { AbstractJSONModel } from "./master-model";
import { endOfDay, startOfDay } from "date-fns";
import LoenserviceHandler, { eSalaryAbsenceStatus } from "@/handlers/loenservice-handler";

export class MaternityCase extends AbstractJSONModel {
  actualDeliveryDate: Date | null = null;
  caseNumber: string = "-1";
  expectedDeliveryDate: Date | null = null;
  maternityType: string = "";
  maternityTypeText: string = "";
  notificationDate: Date | null = null;
}

export interface IAbsence {
  id: number;
  start_date: Date;
  end_date: Date;
  start_time: number | null;
  end_time: number | null;
  absence_type_id: number;
  employee_id: number;
  hours_to_file?: number | null;
  created_at: Date;
  updated_at: Date;
  abs_filing_date?: Date | null;
  corrected: boolean;
  is_open: boolean;
  child_dob_is_actual: boolean;
  child_dob?: Date | null;
  fit_filing_date?: Date | null;
  note: string;
  status: string;
  status_message: string;
  kmd_additional_data: string;
  ist_loen_external_id: string | null;
  absence_request_external_id: string | null;
}

export interface IAbsencePost {
  start_date: Date;
  end_date: Date;
  start_time: number | null;
  end_time: number | null;
  absence_type_id: number;
  employee_id: number;
  hours_to_file?: number | null;
  is_open: boolean;
  child_dob_is_actual: boolean;
  child_dob?: Date | null;
  note: string;
  kmd_additional_data: string;
  absence_request_external_id: string | null;
}

export interface IAbsencePut {
  id: number;
  start_date: Date;
  end_date: Date;
  start_time: number | null;
  end_time: number | null;
  absence_type_id: number;
  hours_to_file?: number | null;
  is_open: boolean;
  child_dob_is_actual: boolean;
  child_dob?: Date | null;
  note: string;
  kmd_additional_data: string;
}

export class Absence extends AbstractJSONModel implements IAbsence {
  public id = -1;
  @Type(() => Date)
  public start_date: Date = new Date();
  @Type(() => Date)
  public end_date: Date = new Date();
  public start_time: number | null = null;
  public end_time: number | null = null;
  public absence_type_id = -1;
  public employee_id = -1;
  public hours_to_file?: number | null = null;
  @Type(() => Date)
  public created_at: Date = new Date();
  @Type(() => Date)
  public updated_at: Date = new Date();
  @Type(() => Date)
  public abs_filing_date?: Date | null = null;
  public corrected = false;
  public is_open = false;
  public child_dob_is_actual = false;
  public child_dob?: Date | null = null;
  @Type(() => Date)
  public fit_filing_date?: Date | null = null;
  public note = "";
  public status: string = LoenserviceHandler.INITIAL;
  public status_message = "";
  public static multipleEmployeesAbsenceCodes = ["AFS", "FE", "6F", "KU", "TJ"];
  public kmd_additional_data = "";
  public ist_loen_external_id: string | null = null;
  public absence_request_external_id: string | null = null;

  equals(other: Absence): boolean {
    return other.id === this.id;
  }

  get Periode(): IPeriode {
    return {
      fra: startOfDay(this.start_date),
      tilOgMed: endOfDay(this.end_date)
    } as IPeriode;
  }

  isAbsenceActiveOnDate(date: Date): boolean {
    return (
      (date >= startOfDay(this.start_date) && this.is_open === true) ||
      (this.is_open === false &&
        date >= startOfDay(this.start_date) &&
        date <= endOfDay(this.end_date))
    );
  }

  getStatus(errorMessage: string = ""): eSalaryAbsenceStatus {
    return LoenserviceHandler.getStatus(this, this.status, errorMessage);
  }

  isExistingAbsence(): boolean {
    return this.id > -1;
  }

  duration(): number {
    if (this.start_time === null || this.end_time === null) {
      return 0.0;
    } else {
      return this.end_time - this.start_time;
    }
  }

  public convertToPostObject(): IAbsencePost {
    const ap: IAbsencePost = Object.assign({});
    ap.absence_type_id = this.absence_type_id;
    ap.child_dob = this.child_dob;
    ap.child_dob_is_actual = this.child_dob_is_actual;
    ap.employee_id = this.employee_id;
    ap.end_date = this.end_date;
    ap.hours_to_file = this.hours_to_file;
    ap.is_open = this.is_open;
    ap.note = this.note;
    ap.start_date = this.start_date;
    ap.start_time = this.start_time;
    ap.end_time = this.end_time;
    ap.kmd_additional_data = this.kmd_additional_data;
    ap.absence_request_external_id = this.absence_request_external_id;
    return ap;
  }

  public convertToPutObject(): IAbsencePut {
    const ap: IAbsencePut = Object.assign({});
    ap.id = this.id;
    ap.absence_type_id = this.absence_type_id;
    ap.child_dob = this.child_dob;
    ap.child_dob_is_actual = this.child_dob_is_actual;
    ap.end_date = this.end_date;
    ap.hours_to_file = this.hours_to_file;
    ap.is_open = this.is_open;
    ap.note = this.note;
    ap.start_date = this.start_date;
    ap.start_time = this.start_time;
    ap.end_time = this.end_time;
    ap.kmd_additional_data = this.kmd_additional_data;
    return ap;
  }
}
